/**
 * The configurator's only transport. It has no backend — its "server" is
 * whatever origin serves the models, textures and logos a config points at —
 * but the rule is the same either way: requests go out from here, not from a
 * bare `fetch` in a component, a hook or the scene.
 */

/** Falls back to the file name the URL already carries. */
function fileNameFor(assetUrl: string, fileName?: string): string {
  if (fileName) return fileName;
  return assetUrl.split("/").pop() || "model.glb";
}

/**
 * Fetches an asset and hands it to the browser as a download.
 *
 * The object URL is revoked immediately after the click: the browser has
 * already taken its own reference to the blob by then, and holding ours would
 * leak the whole GLB for the life of the page.
 */
export async function downloadAsset(
  assetUrl: string,
  fileName?: string,
): Promise<void> {
  const response = await fetch(assetUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to download ${assetUrl}: ${response.status} ${response.statusText}`,
    );
  }

  const objectUrl = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileNameFor(assetUrl, fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
